import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

type LoggerState = {
  enabledLoggers: string[];
  allLoggersEnabled: boolean;
  toggleLogger: (what: string) => boolean;
  toggleAllLoggers: () => boolean;
};

const useLoggerState = create(
  persist<LoggerState>(
    (set, get) => ({
      enabledLoggers: [],
      allLoggersEnabled: false,
      toggleLogger: (what: string): boolean => {
        const current = get().enabledLoggers;
        const isEnabled = current.includes(what);
        set({
          enabledLoggers: isEnabled
            ? current.filter((l) => l !== what)
            : [...current, what],
        });
        return !isEnabled;
      },
      toggleAllLoggers: (): boolean => {
        const newValue = !get().allLoggersEnabled;
        set({ allLoggersEnabled: newValue });
        return newValue;
      },
    }),
    {
      name: 'logger-state',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

const getEnabledLoggers = (): string[] =>
  useLoggerState.getState().enabledLoggers;

const getAllLoggersEnabled = (): boolean =>
  useLoggerState.getState().allLoggersEnabled;

export const toggleLogger = (what: string): boolean =>
  useLoggerState.getState().toggleLogger(what);

export const toggleAllLoggers = (): boolean =>
  useLoggerState.getState().toggleAllLoggers();

type LoggerOptions = {
  color?: string;
};

export const makeLogger = (
  title: string,
  options?: LoggerOptions
): ((...args: unknown[]) => void) => {
  return (...args: unknown[]): void => {
    if (!getAllLoggersEnabled() && !getEnabledLoggers().includes(title)) {
      return;
    }
    const prefix = `${new Date().toISOString()} [${title}]:`;

    if (options?.color) {
      // eslint-disable-next-line no-console
      console.log(`%c${prefix}`, `color: ${options.color}`, ...args);
    } else {
      // eslint-disable-next-line no-console
      console.log(prefix, ...args);
    }
  };
};
